import {
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword,
} from 'aws-amplify/auth';

import { Injectable, signal } from '@angular/core';

export interface User {
  userId: string;
  username: string;
  email?: string;
}

const AUTH_CACHE_KEY = 'ftp_auth_cache';
const CACHE_TTL_MS = 45 * 60 * 1000; // 45 minutes — within Cognito's 1h access token lifetime

interface AuthCache {
  user: User;
  token: string;
  expiresAt: number;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  user = signal<User | null>(null);
  token = signal<string | null>(null);

  // Shared promise — deduplicates concurrent calls (e.g. APP_INITIALIZER + guard)
  private initPromise: Promise<void> | null = null;

  initAuth(): Promise<void> {
    if (!this.initPromise) {
      this.initPromise = this.checkAuth();
    }
    return this.initPromise;
  }

  async login(email: string, password: string): Promise<void> {
    const result = await signIn({ username: email, password });

    if (result.isSignedIn !== true) {
      throw new Error('Login not completed. Additional steps required.');
    }

    const [currentUser, session] = await Promise.all([getCurrentUser(), fetchAuthSession()]);

    const user: User = {
      userId: currentUser.userId,
      username: currentUser.username,
      email: currentUser.signInDetails?.loginId,
    };
    const token = session.tokens?.accessToken?.toString() ?? null;

    this.user.set(user);
    this.token.set(token);

    if (token) {
      this.saveCache(user, token);
    }
  }

  async logout(): Promise<void> {
    await signOut();
    this.user.set(null);
    this.token.set(null);
    this.clearCache();
    this.initPromise = null;
  }

  async checkAuth(): Promise<void> {
    const cached = this.readCache();

    if (cached) {
      this.user.set(cached.user);
      this.token.set(cached.token);
      this.backgroundRefresh();
      return;
    }

    try {
      const [currentUser, session] = await Promise.all([getCurrentUser(), fetchAuthSession()]);

      const user: User = {
        userId: currentUser.userId,
        username: currentUser.username,
        email: currentUser.signInDetails?.loginId,
      };
      const token = session.tokens?.accessToken?.toString() ?? null;

      this.user.set(user);
      this.token.set(token);

      if (token) {
        this.saveCache(user, token);
      }
    } catch {
      this.user.set(null);
      this.token.set(null);
      this.clearCache();
    }
  }

  isAuthenticated(): boolean {
    return !!this.token();
  }

  async forgotPassword(email: string): Promise<void> {
    await resetPassword({ username: email });
  }

  async confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
    await confirmResetPassword({
      username: email,
      confirmationCode: code,
      newPassword,
    });
  }

  // Validates session with AWS after serving from cache — does not block rendering
  private backgroundRefresh(): void {
    Promise.all([getCurrentUser(), fetchAuthSession()])
      .then(([currentUser, session]) => {
        const user: User = {
          userId: currentUser.userId,
          username: currentUser.username,
          email: currentUser.signInDetails?.loginId,
        };
        const token = session.tokens?.accessToken?.toString() ?? null;

        this.user.set(user);
        this.token.set(token);

        if (token) {
          this.saveCache(user, token);
        } else {
          this.user.set(null);
          this.clearCache();
        }
      })
      .catch(() => {
        // Session expired — clear state; guard will redirect on next navigation
        this.user.set(null);
        this.token.set(null);
        this.clearCache();
      });
  }

  private readCache(): AuthCache | null {
    try {
      const raw = localStorage.getItem(AUTH_CACHE_KEY);
      if (!raw) return null;

      const cached: AuthCache = JSON.parse(raw);

      if (Date.now() > cached.expiresAt) {
        localStorage.removeItem(AUTH_CACHE_KEY);
        return null;
      }

      return cached;
    } catch {
      return null;
    }
  }

  private saveCache(user: User, token: string): void {
    try {
      const cache: AuthCache = { user, token, expiresAt: Date.now() + CACHE_TTL_MS };
      localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(cache));
    } catch {
      // localStorage may be unavailable (private browsing, quota exceeded)
    }
  }

  private clearCache(): void {
    try {
      localStorage.removeItem(AUTH_CACHE_KEY);
    } catch {
      // ignore
    }
  }
}
